import React from 'react';
import { Calculator, ArrowRight, Globe2, Database, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../lib/theme-context';

function useInView(ref: React.RefObject<HTMLElement | null>) {
  const [isInView, setIsInView] = React.useState(false);
  React.useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsInView(true); },
      { threshold: 0.15 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
  return isInView;
}

export function BudgetSimulatorPromo() {
  const { theme } = useTheme();
  const sectionRef = React.useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef);

  const screenshotUrl = theme === 'dark'
    ? "https://pvtistes.net/wp-content/uploads/2025/02/budget1-sombre.jpg"
    : "https://pvtistes.net/wp-content/uploads/2025/02/budget1.jpg";

  return (
    <section ref={sectionRef} className="py-16 sm:py-24 bg-white dark:bg-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`relative overflow-hidden rounded-3xl bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 transition-all duration-700 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-accent-500/10 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/4" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-primary-500/10 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/4" />
          </div>

          <div className="relative grid lg:grid-cols-2 gap-8 lg:gap-0">
            <div className="p-8 sm:p-12 lg:p-16 flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-500/20 text-accent-300 text-sm font-medium mb-6 w-fit">
                <Calculator className="w-4 h-4" />
                <span>Simulateur de budget</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
                Planifiez votre budget<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-400 to-accent-300">avec pr&eacute;cision</span>
              </h2>

              <p className="text-lg text-gray-400 mb-10 max-w-lg leading-relaxed">
                Des donn&eacute;es r&eacute;elles et actualis&eacute;es pour estimer vos d&eacute;penses. Mode nomade disponible avec options d'h&eacute;bergement flexibles.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                {[
                  { icon: Database, label: 'Donn\u00e9es r\u00e9elles', desc: 'Prix \u00e0 jour' },
                  { icon: TrendingUp, label: 'Mode nomade', desc: 'Van, auberges...' },
                  { icon: Globe2, label: 'Tous les pays', desc: 'Toutes destinations' },
                ].map((feat) => (
                  <div key={feat.label} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-3">
                    <feat.icon className="w-5 h-5 text-accent-400 flex-shrink-0" />
                    <div>
                      <div className="text-sm font-medium text-white">{feat.label}</div>
                      <div className="text-xs text-gray-500">{feat.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              <Link
                to="/simulateur-budget"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-accent-500 hover:bg-accent-600 text-white rounded-xl font-semibold shadow-xl shadow-accent-500/20 hover:shadow-2xl hover:shadow-accent-500/30 transition-all duration-300 hover:-translate-y-0.5 w-fit"
              >
                <span>Essayer le simulateur</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            <div className="relative hidden lg:flex items-center justify-center p-8">
              <div className="relative w-[280px] aspect-[9/19] bg-black rounded-[3rem] p-3 shadow-2xl -rotate-3">
                <div className="absolute inset-0 rounded-[3rem] border-[14px] border-gray-800">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-6 bg-black rounded-b-2xl" />
                </div>
                <div className="relative w-full h-full rounded-[2rem] overflow-hidden bg-gray-100">
                  <img
                    src={screenshotUrl}
                    alt="Budget simulator preview"
                    className="absolute inset-0 w-full h-full object-cover object-top"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>

              <div className="absolute top-16 right-16 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-4 max-w-[200px]">
                <div className="text-xs text-gray-400 mb-1">Budget mensuel</div>
                <div className="text-2xl font-bold text-white mb-2">1,850 &euro;</div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div className="w-3/4 h-full bg-gradient-to-r from-accent-400 to-accent-500 rounded-full" />
                </div>
              </div>

              <div className="absolute bottom-16 right-8 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-4 max-w-[180px]">
                <div className="text-xs text-gray-400 mb-1">&Eacute;conomies</div>
                <div className="text-xl font-bold text-white mb-2">5,000 &euro;</div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div className="w-1/2 h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full" />
                </div>
              </div>
            </div>

            <div className="lg:hidden relative h-56 overflow-hidden">
              <img
                src={screenshotUrl}
                alt="Budget simulator preview"
                className="absolute inset-0 w-full h-full object-cover object-top"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/40 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-3 flex items-center justify-between">
                  <div>
                    <div className="text-xs text-gray-400">Budget mensuel</div>
                    <div className="text-lg font-bold text-white">1,850 &euro;</div>
                  </div>
                  <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
                    <div className="w-3/4 h-full bg-accent-500 rounded-full" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
